<template>
  <el-dialog
    class="dialog-wrapper roles-modal"
    :visible="visible"
    :show-close="false"
    @close="abortCreate"
  >
    <ContentHeader
      slot="title"
      :title="
        isModalEdit
          ? $t('roles.update.updateRole')
          : $t('roles.create.createRole')
      "
    />
    <ValidationObserver
      ref="roleForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="6">
            <FormItem
              rules="required"
              :label="$t('roles.create.name')"
            >
              <el-input v-model="formData.name" />
            </FormItem>
          </el-col>
        </el-row>

        <p class="roles-modal__permissions-text">
          {{ $t('roles.create.permissions') }}
        </p>

        <el-row
          :gutter="20"
          type="flex"
          class="roles-modal__permissions-row"
        >
          <el-col
            v-for="(permission, index) in permissionsList"
            :key="index"
            :span="12"
            class="roles-modal__permissions-col"
          >
            <el-checkbox
              v-if="permission.children"
              class="roles-modal__permission-category"
              :value="groupIsChecked(index)"
              @change="checkAllGroup(index)"
              >{{ $t(`permissions.${permission.label}`) }}</el-checkbox
            >
            <el-checkbox-group
              v-model="formData.permissions"
              v-if="permission.children"
            >
              <el-checkbox
                v-for="child in permission.children"
                :key="child.value"
                :label="child.value"
                class="roles-modal__permission-item"
                >{{ $t(`permissions.${child.label}`) }}</el-checkbox
              >
            </el-checkbox-group>

            <el-checkbox-group
              v-model="formData.permissions"
              v-if="!permission.children"
            >
              <el-checkbox
                :label="permission.value"
                class="roles-modal__permission-category"
                >{{ $t(`permissions.${permission.label}`) }}</el-checkbox
              >
            </el-checkbox-group>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortCreate"
          >
            {{ $t('roles.create.abortCreate') }}
          </el-button>
          <el-button
            v-if="isModalEdit"
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(updateRole)"
          >
            {{ $t('roles.update.updateRole') }}
          </el-button>
          <el-button
            v-else
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(createRole)"
          >
            {{ $t('roles.create.createRole') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { rolesApi } from '@/api';
  import { permissionsList } from '@/config/constants';
  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';

  const defaultData = {
    name: '',
    permissions: [],
  };

  export default {
    name: 'CreateEditRole',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
    },
    props: {
      visible: { type: Boolean, default: false },
      isModalEdit: { type: Boolean, default: false },
      initialData: { type: Object, default: () => defaultData },
    },
    data() {
      return {
        formData: { ...defaultData },
        permissionsList,
      };
    },
    watch: {
      initialData(data) {
        if (Object.keys(data).length > 0) {
          this.formData = { ...data };
        }
      },
    },
    methods: {
      async createRole() {
        try {
          await rolesApi.create(this.formData);
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('roles.create.success'),
          });
          this.formData = { ...defaultData };
          this.$refs.roleForm.reset();
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async updateRole() {
        try {
          await rolesApi.patch(this.$route.params.roleName, {
            permissions: this.formData.permissions,
          });
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('roles.update.success'),
          });
          this.formData = { ...defaultData };
          this.$refs.roleForm.reset();
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      abortCreate() {
        this.formData = { ...defaultData };
        this.$refs.roleForm.reset();
        this.$emit('dialogClose', { success: false });
      },
      groupIsChecked(index) {
        const groupPermissions = this.permissionsList[index].children.map(
          (c) => c.value
        );
        return groupPermissions.every((p) =>
          this.formData.permissions.includes(p)
        );
      },
      checkAllGroup(index) {
        const groupPermissions = this.permissionsList[index].children.map(
          (c) => c.value
        );
        const groupChecked = this.groupIsChecked(index);
        this.formData.permissions = this.formData.permissions.filter(
          (p) => !groupPermissions.includes(p)
        );
        if (!groupChecked) {
          this.formData.permissions = [
            ...this.formData.permissions,
            ...groupPermissions,
          ];
        }
      },
    },
  };
</script>

<style lang="scss">
  .roles-modal {
    &__permissions-text {
      padding-bottom: 1rem;
    }

    &__permissions-row {
      flex-wrap: wrap;
    }

    &__permissions-col {
      margin-bottom: 1.25rem;
    }

    &__permission-category {
      margin-bottom: 0.5rem;
    }

    &__permission-item {
      padding-left: 0.5rem;
      display: block;
    }
  }
</style>
